import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const MoreDetailsFAQ = () => {
  const [activeTab, setActiveTab] = useState('scalable');
  const [openFAQ, setOpenFAQ] = useState('learning-progression');

  const tabData = {
    scalable: {
      title: 'Scalable Lab',
      content: [
        { title: 'Sensor & IoT Integration:', description: 'Easily add sensors and IoT kits to support advanced AI and automation experiments.' },
        { title: 'Custom Design Support:', description: 'Lab setup and tools can be tailored for faculty research, student innovation, or specific departmental needs.' },
        { title: 'Modular Expansion:', description: 'Ready to incorporate future technologies like autonomous systems & LLM.' },
        { title: 'Upgrade-Friendly:', description: 'Scales from basic learning environments to advanced research setups with minimal effort.' }
      ]
    },
    ideal: {
      title: 'Ideal For All',
      content: [
        { title: 'Educational Institutions:', description: 'Perfect for schools, colleges, and universities looking to integrate AI education.' },
        { title: 'Research Organizations:', description: 'Suitable for research labs and innovation centers requiring flexible AI tools.' },
        { title: 'Training Centers:', description: 'Ideal for professional development and corporate training programs.' },
        { title: 'Individual Learners:', description: 'Great for students and professionals who want to learn AI independently.' }
      ]
    }
  };

  const faqData = [
    { id: 'traditional-labs', question: 'What Makes The AI Kit Better Than Traditional Labs?', answer: 'The AI Kit offers portability, affordability, and comprehensive integration that traditional labs cannot match. It combines multiple AI disciplines in one device, costs 10x less than conventional setups, and requires no complex installation or maintenance.' },
    { id: 'hardware-connection', question: 'Can It Connect With Extra Hardware?', answer: 'Yes, the AI Kit supports extensive hardware integration including sensors, IoT devices, robotic components, and external processing units. It features multiple connectivity options including USB, Bluetooth, and Wi-Fi for seamless hardware expansion.' },
    { id: 'learning-progression', question: 'How Learning Progression Happens?', answer: 'Students begin with intuitive visual interfaces to understand core AI principles. As they progress, they are introduced to Python coding, dataset training, and algorithm design, moving into areas like IoT integration, robotics control, computer vision, and NLP. Advanced learners can build custom projects, enabling a seamless transition from foundational knowledge to innovation.' }
  ];
  
  // Base styles for the component
  const styles = {
    container: {
      backgroundColor: '#0a0a0a',
      color: '#ffffff',
      padding: '6rem 2rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    contentWrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    mainLayout: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '4rem'
    },
    detailsContainer: {
      flex: '1',
      position: 'sticky',
      top: '2rem'
    },
    detailsCard: {
      backgroundColor: '#1a1a1a',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
      border: '1px solid #2a2a2a'
    },
    detailsHeader: {
      textAlign: 'center',
      padding: '2rem 2rem 1rem 2rem'
    },
    detailsTitle: {
      fontSize: '2rem',
      fontWeight: '700',
      margin: '0',
      color: '#ffffff'
    },
    titleUnderline: {
      width: '60px',
      height: '3px',
      backgroundColor: '#6366f1',
      margin: '1rem auto 0',
      borderRadius: '2px'
    },
    tabContainer: {
      display: 'flex',
      backgroundColor: '#0a0a0a',
      margin: '0 1rem',
      borderRadius: '12px',
      overflow: 'hidden',
      border: '1px solid #2a2a2a'
    },
    tabButton: {
      flex: 1,
      padding: '1rem 1.5rem',
      border: 'none',
      backgroundColor: 'transparent',
      color: '#9ca3af',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontSize: '1rem'
    },
    tabContent: {
      padding: '2rem',
      backgroundColor: '#1a1a1a'
    },
    detailsItem: {
      marginBottom: '1.5rem',
      padding: '1rem',
      backgroundColor: '#252525',
      borderRadius: '12px',
      border: '1px solid #2a2a2a',
      transition: 'all 0.3s ease'
    },
    detailsItemContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px'
    },
    detailsBullet: {
      width: '6px',
      height: '6px',
      backgroundColor: '#6366f1',
      borderRadius: '50%',
      marginTop: '8px',
      flexShrink: 0
    },
    detailsItemText: {
      title: { fontWeight: '600', color: '#ffffff', marginRight: '8px' },
      description: { color: '#9ca3af', lineHeight: '1.6' }
    },
    faqContainer: {
      flex: '1.2',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    faqHeader: {
      textAlign: 'center',
      marginBottom: '2rem'
    },
    faqTitle: {
      fontSize: '2rem',
      fontWeight: '700',
      margin: '0',
      color: '#ffffff'
    },
    faqItem: {
      backgroundColor: '#1a1a1a',
      border: '1px solid #2a2a2a',
      borderRadius: '16px',
      overflow: 'hidden',
      transition: 'all 0.3s ease'
    },
    faqButton: {
      width: '100%',
      padding: '1.5rem 2rem',
      border: 'none',
      backgroundColor: '#1a1a1a',
      textAlign: 'left',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#ffffff',
      transition: 'all 0.3s ease'
    },
    faqAnswerContainer: {
      padding: '0 2rem 2rem',
      backgroundColor: '#1a1a1a'
    },
    faqAnswer: {
      color: '#9ca3af',
      lineHeight: '1.7',
      fontSize: '1rem',
      margin: '0',
      textAlign: 'justify',
      animation: 'fadeIn 0.4s ease-in-out'
    }
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          /* Hover & Active States */
          [data-component="details-item"]:hover {
            background-color: #2a2a2a !important;
            border-color: #6366f1 !important;
          }
          
          /* Responsive Layout */
          @media (max-width: 992px) {
            [data-component="main-layout"] {
              flex-direction: column !important;
              gap: 3rem !important;
            }
            [data-component="details-container"] {
              position: static !important; /* Disables sticky positioning on mobile */
              width: 100%;
            }
          }
          
          @media (max-width: 768px) {
            [data-component="container"] {
              padding: 4rem 1rem !important;
            }
            [data-component="details-title"], [data-component="faq-title"] {
              font-size: 1.75rem !important;
            }
            [data-component="tab-button"], [data-component="faq-button"] {
              font-size: 1rem !important;
              padding: 0.8rem 1rem !important;
            }
          }
        `}
      </style>

      <div style={styles.contentWrapper}>
        <div style={styles.mainLayout} data-component="main-layout">
          
          <div style={styles.detailsContainer} data-component="details-container">
            <div style={styles.detailsCard}>
              <div style={styles.detailsHeader}>
                <h2 style={styles.detailsTitle} data-component="details-title">More Details</h2>
                <div style={styles.titleUnderline}></div>
              </div>

              <div style={styles.tabContainer}>
                <button
                  onClick={() => setActiveTab('scalable')}
                  style={{...styles.tabButton, backgroundColor: activeTab === 'scalable' ? '#6366f1' : 'transparent', color: activeTab === 'scalable' ? '#ffffff' : '#9ca3af'}}
                  data-component="tab-button"
                >
                  📊 Scalable Lab
                </button>
                <button
                  onClick={() => setActiveTab('ideal')}
                  style={{...styles.tabButton, backgroundColor: activeTab === 'ideal' ? '#6366f1' : 'transparent', color: activeTab === 'ideal' ? '#ffffff' : '#9ca3af'}}
                  data-component="tab-button"
                >
                  🎯 Ideal For All
                </button>
              </div>

              <div style={styles.tabContent}>
                {tabData[activeTab].content.map((item, index) => (
                  <div key={index} style={styles.detailsItem} data-component="details-item">
                    <div style={styles.detailsItemContent}>
                      <div style={styles.detailsBullet}></div>
                      <div>
                        <span style={styles.detailsItemText.title}>{item.title}</span>
                        <span style={styles.detailsItemText.description}>{item.description}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={styles.faqContainer}>
            <div style={styles.faqHeader}>
              <h2 style={styles.faqTitle} data-component="faq-title">FAQ</h2>
              <div style={styles.titleUnderline}></div>
            </div>

            {faqData.map((faq) => (
              <div key={faq.id} style={{...styles.faqItem, boxShadow: openFAQ === faq.id ? '0 10px 30px rgba(99, 102, 241, 0.2)' : 'none'}}>
                <button
                  onClick={() => setOpenFAQ(openFAQ === faq.id ? null : faq.id)}
                  style={{...styles.faqButton, backgroundColor: openFAQ === faq.id ? '#6366f1' : '#1a1a1a'}}
                  data-component="faq-button"
                >
                  <span>{faq.question}</span>
                  <div style={{ color: openFAQ === faq.id ? '#ffffff' : '#6366f1', marginLeft: '1rem' }}>
                    {openFAQ === faq.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </button>
                
                {openFAQ === faq.id && (
                  <div style={styles.faqAnswerContainer}>
                    <p style={{...styles.faqAnswer, paddingTop: '1.5em'}}>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoreDetailsFAQ;